import React, { useState, useEffect } from 'react';
import { Dropdown, Tooltip, Layout, Popover } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import IconAndName from '../atoms/side-navbar/IconAndName';
import Stepper from '../atoms/side-navbar/Stepper';
import NavTooltip from '../atoms/side-navbar/NavTooltip';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  toggleNewTaskModal,
  toggleNewProjectModal,
  toggleNewTeamChatsModal,
  toggleWorkspaceFieldsModal,
} from '../../../actions';
import NavBarContainer from '../molecules/side-navbar/NavBarContainer';
import SwitchWorkspacePanel from '../molecules/side-navbar/SwitchWorkspacePanel';
import { truncateString, getWorkspacePalette } from '../../../js/helper';

import NotificationTooltip from '../molecules/tooltips/NotificationTooltip';
import ChatsTooltip from '../molecules/tooltips/ChatsTooltip';
import ProjectsTooltip from '../molecules/tooltips/ProjectsTooltip';
import Icon from '../atoms/global/Icon';
import { isAIDrawerOpen, isUserDrawerOpen } from '../../../actions/toggleAction';
import { getUserLicense } from '../../../js/functions';
import { toggleInviteToWorkspaceModal, toggleWorkspaceTagsModal } from '../../../actions/modalAction';
import useNavigateWithPersistedParams from '../../../js/useNavigateWithPersistedParams';
import { useLocation } from 'react-router-dom';

const ROUTE_NAV_ITEMS = ['/activity', '/reminders', '/all-tasks', '/dashboard', '/portfolio', '/forms', '/calendar'];

const LeftNavBar = (props) => {
  const {
    workspaces,
    myProjects,
    teams,
    chats,
    setIsLoading,
    workspaceRole,
    isMobile,
    setSidebarOpen,
  } = props;
  const navigate = useNavigateWithPersistedParams();
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();

  const isGuest = workspaceRole == 'guest';
  const isMember = workspaceRole == 'member' || isGuest;

  const toggle = useSelector((state) => state.navReducer.toggle);
  const loggedUser = useSelector((state) => state.userReducer.user);

  const [nav, setNav] = useState(false);
  const [selectedLeftNav, setSelectedLeftNav] = useState("Home");
  const [currentWorkspace, setCurrentWorkspace] = useState(props.currentWorkspace);

  const [unredNotifications, setUnredNotifications] = useState(0);
  const [unredChatNotifications, setUnredChatNotifications] = useState(0);

  const [localChats, setLocalChats] = useState(chats);
  const [localTeams, setLocalTeams] = useState(teams);
  const [localProjects, setLocalProjects] = useState(myProjects);

  const toggleWorkspacePanel = () => {
    setNav((nav) => !nav);
  };

  useEffect(() => {
    setCurrentWorkspace(props?.currentWorkspace);
    setLocalTeams(teams);
    setLocalChats(chats);
    setLocalProjects(myProjects);
  }, [props?.currentWorkspace, workspaces, chats, teams, myProjects]);

  useEffect(() => {
    setUnredChatNotifications(0);
    setUnredNotifications((props.unreadNotificationCount ?? 0) + (props?.pendingInvites?.length ?? 0));
  }, [props.unreadNotificationCount, props.pendingInvites])

  const workspaceName = truncateString(currentWorkspace?.title, 22) || intl.formatMessage({ id: 'MY_WORKSPACE', defaultMessage: 'My workspace' });
  const iconLetters = workspaceName?.slice(0, 2)?.toUpperCase();
  const wsPalette = getWorkspacePalette(currentWorkspace?.title || '');

  const me = currentWorkspace?.members?.find(m => m?._id?.toString() === loggedUser?._id?.toString());
  const isOwnerOrAdmin = me?.role == 'owner' || me?.role == 'admin';

  
  useEffect(() => {
    if (ROUTE_NAV_ITEMS.includes(location.pathname)) {
      setSelectedLeftNav('Home');
    }
  }, [location.pathname]);

  const isNavActive = (name) => {
    const onRoutePage = ROUTE_NAV_ITEMS.includes(location.pathname);
    switch (name) {
      case 'Activity':   return location.pathname === '/activity';
      case 'Reminders':  return location.pathname === '/reminders';
      case 'Tasks':      return location.pathname === '/all-tasks';
      case 'Dashboard': return location.pathname === '/dashboard';
      case 'Portfolio': return location.pathname === '/portfolio';
      case 'Forms':     return location.pathname === '/forms';
      case 'Calendar':  return location.pathname === '/calendar';
      default:          return !onRoutePage && selectedLeftNav === name;
    }
  };

  const navTooltipOnClick = (name) => {
    setSelectedLeftNav(name);
  }

  const NavIcon = (name, src, css, path, height = 16, items = [], line = 22) => {
    return (
      <>
        <IconAndName
          name={name}
          phase='sideNav'
          src={`${src}.svg`}
          height={height}
          classesForParentDiv={css}
          class={'font-14 leading-3 align-center'}
          path={path}
          onPress={() => {
            if (name == 'Settings') {
              dispatch(isUserDrawerOpen(true)); setSidebarOpen?.(false);
            } else if (name == 'Workflo AI') {
              dispatch(isAIDrawerOpen(true))
            } else {
              navigate(path);
            }
          }}
        />
        <Stepper items={items} line={line} />
      </>
    );
  };

  const homeLinks = () => {
    const license = getUserLicense(loggedUser, sessionStorage.getItem("organization") || localStorage.getItem("organization")) || 'Free Plan';
    return (
      <div style={{ padding: 10 }}>
        {NavIcon(intl.formatMessage({ id: 'HOME', defaultMessage: 'Home' }), 'home', 'nav-item dashboardNavLink', `/${currentWorkspace?._id}/`, 20, [
          {
            label: intl.formatMessage({ id: isMember ? 'MEMBERS' : 'INVITE_MEMBERS', defaultMessage: 'Invite Members' }),
            onPress: async () => {
              await dispatch(toggleInviteToWorkspaceModal(true));
              setNav(false);
            },
            active: false
          },
          {
            label: intl.formatMessage({ id: 'CUSTOM_FIELDS', defaultMessage: 'Custom Fields' }),
            onPress: () => {
              dispatch(toggleWorkspaceFieldsModal(true))
            },
            active: false
          },
          {
            label: intl.formatMessage({ id: 'TAGS', defaultMessage: 'Tags' }),
            onPress: () => {
              dispatch(toggleWorkspaceTagsModal(true))
            },
            active: false
          },
        ])}
        {NavIcon(intl.formatMessage({ id: 'ACTIVITY', defaultMessage: 'Activity' }), 'notificationWhite', 'nav-item teamsyNavLink', '/activity', 20)}
        {NavIcon(intl.formatMessage({ id: 'TASKS', defaultMessage: 'Taks' }), 'tasks-tooltip', 'nav-item homeNavLink', '/all-tasks', 20)}
        {NavIcon(intl.formatMessage({ id: 'DASHBOARD', defaultMessage: 'Dashboard' }), 'dashboardWhite', 'nav-item teamsyNavLink', '/dashboard', 20)}
        {NavIcon(intl.formatMessage({ id: 'PORTFOLIO', defaultMessage: 'Portfolio' }), 'box', 'nav-item teamsyNavLink', '/portfolio', 20)}
        {NavIcon(intl.formatMessage({ id: 'FORMS', defaultMessage: 'Forms' }), 'formNavWhite', 'nav-item', '/forms', 20)}
        {NavIcon(intl.formatMessage({ id: 'PAYMENTS', defaultMessage: 'Payments' }), 'tagWhite', 'nav-item teamsyNavLink', '/payments', 20)}
        {NavIcon(intl.formatMessage({ id: 'CALENDAR', defaultMessage: 'Calendar' }), 'calendar', 'nav-item teamsyNavLink', '/calendar', 20)}
        {/* {license != 'Free Plan' && NavIcon(intl.formatMessage({ id: 'WORKFLO_AI', defaultMessage: 'Workflo AI' }), 'custom-fields-white', 'nav-item aiNavLink', '/AI', 20)} */}
        {NavIcon(intl.formatMessage({ id: 'SETTINGS', defaultMessage: 'Settings' }), 'settingsWhite', 'nav-item teamsyNavLink', '/account-management', 20)}
      </div>
    );
  }

  const setShowTaskModal = (value) => {
    dispatch(toggleNewTaskModal({
      value: value
    }));
    if (value) setSidebarOpen?.(false);
  };

  const setShowProjectModal = (value) => {
    dispatch(toggleNewProjectModal({
      value: value
    }));
    if (value) setSidebarOpen?.(false);
  };

  const setIsTeamsChatModalOpen = (value, isTeams) => {
    dispatch(toggleNewTeamChatsModal({
      value: value,
      isTeams: isTeams
    }));
  }

  const { Sider } = Layout;
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className='side-bar-container d-flex' style={{ height: '100%' }}>
      {!collapsed && <div className='quick-side-menu'>
        {collapsed &&
          <Tooltip title={intl.formatMessage({ id: 'SHOW_MENU', defaultMessage: 'Show Menu' })}>
            <div className='hover:opacity-60 mb-10'>
              <Icon name='minimize.svg' onClick={() => setCollapsed(false)} />
            </div>
          </Tooltip>}
        <div className='square-letter'
          onClick={toggleWorkspacePanel}
          style={{
            color: wsPalette.text,
            fontWeight: 700,
            background: wsPalette.bg,
            border: `1px solid ${wsPalette.border}`,
            height: 34,
            width: 34,
            fontSize: '12px',
            marginBottom: '1rem',
            boxShadow: 'none',
            marginTop: toggle ? '0' : '1.5rem',
            borderRadius: 8,
          }}>
          {iconLetters}
        </div>
        {nav ?
          <SwitchWorkspacePanel toggleWorkspacePanel={toggleWorkspacePanel} workspaces={workspaces} setIsLoading={setIsLoading} />
          :
          <div className='flex flex-col h-full justify-between align-center w-full'>
            <div>
              <NavTooltip
                name={intl.formatMessage({ id: 'HOME', defaultMessage: 'Home' })}
                src={'home'}
                classesForParentDiv={`side-nav-item ${isNavActive('Home') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate(`/${currentWorkspace?._id}/`); }} />
              <div className='h-px bg-white/10 mx-2 my-1' />
              <NavTooltip
                name={intl.formatMessage({ id: 'ACTIVITY', defaultMessage: 'Activity' })}
                src={'mail'}
                classesForParentDiv={`side-nav-item ${isNavActive('Activity') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/activity'); }}
                notificationCount={unredNotifications}
                isActivity
                tooltipComponent={<NotificationTooltip notifications={null} />} />
              <NavTooltip
                name={intl.formatMessage({ id: 'MY_REMINDERS', defaultMessage: 'Reminders' })}
                src={'alarm'}
                classesForParentDiv={`side-nav-item ${isNavActive('Reminders') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/reminders'); }} />
              <NavTooltip
                name={intl.formatMessage({ id: 'PROJECTS', defaultMessage: 'Projects' })}
                src={'projects'}
                classesForParentDiv={`side-nav-item ${isNavActive('Projects') ? 'selected' : ''}`}
                onClick={() => navTooltipOnClick('Projects')}
                tooltipComponent={<ProjectsTooltip projects={myProjects} />} />
              <NavTooltip
                name={intl.formatMessage({ id: 'CHATS', defaultMessage: 'Chats' })}
                src={'chat-tooltip'}
                classesForParentDiv={`side-nav-item ${isNavActive('Chats') ? 'selected' : ''}`}
                onClick={() => navTooltipOnClick('Chats')}
                notificationCount={unredChatNotifications || 0}
                tooltipComponent={<ChatsTooltip chats={chats} />} />
              <NavTooltip
                name={intl.formatMessage({ id: 'TASKS', defaultMessage: 'Tasks' })}
                src={'tasks-tooltip'}
                classesForParentDiv={`side-nav-item ${isNavActive('Tasks') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/all-tasks'); }} />
              <div className='h-px bg-white/10 mx-2 my-1' />
              <NavTooltip
                name={intl.formatMessage({ id: 'DASHBOARD', defaultMessage: 'Dashboard' })}
                src={'dashboardWhite'}
                classesForParentDiv={`side-nav-item ${isNavActive('Dashboard') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/dashboard'); }} />
              <NavTooltip
                name={intl.formatMessage({ id: 'PORTFOLIO', defaultMessage: 'Portfolio' })}
                src={'box'}
                classesForParentDiv={`side-nav-item ${isNavActive('Portfolio') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/portfolio'); }} />
              <NavTooltip
                name={intl.formatMessage({ id: 'FORMS', defaultMessage: 'Forms' })}
                src={'formNavWhite'}
                classesForParentDiv={`side-nav-item ${isNavActive('Forms') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/forms'); }} />
              <NavTooltip
                name={intl.formatMessage({ id: 'CALENDAR', defaultMessage: 'Calendar' })}
                src={'calendar'}
                classesForParentDiv={`side-nav-item ${isNavActive('Calendar') ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); navigate('/calendar'); }} />
              <NavTooltip
                name={intl.formatMessage({ id: 'SETTINGS', defaultMessage: 'Settings' })}
                src={'settingsWhite'}
                classesForParentDiv={`side-nav-item ${selectedLeftNav === 'Settings' ? 'selected' : ''}`}
                onClick={() => { navTooltipOnClick('Home'); dispatch(isUserDrawerOpen(true)); setSidebarOpen?.(false); }} />
            </div>
            {!toggle && <Dropdown
              trigger={['click']}
              className='mb-6 h-7'
              placement='topRight'
              menu={{
                items: [
                  {
                    key: 'projects-modal',
                    label: <div className='font-16 p-2'><FormattedMessage id="NEW_PROJECT" defaultMessage="New Project" /></div>,
                    onClick: () => setShowProjectModal(true)
                  },
                  {
                    key: 'chats-modal',
                    label: <div className='font-16 p-2'><FormattedMessage id="NEW_CHAT" defaultMessage="New Chat" /></div>,
                    onClick: () => setIsTeamsChatModalOpen(true, false)
                  },
                  // {
                  //   key: 'teams-modal',
                  //   label: <div className='font-16 p-2'>New Team</div>,
                  //   onClick: () => setIsTeamsChatModalOpen(true, true)
                  // },
                  {
                    key: 'task-modal',
                    label: <div className='font-16 p-2'><FormattedMessage id="NEW_TASK" defaultMessage="New Task" /></div>,
                    onClick: () => setShowTaskModal(true)
                  }
                ],
              }}
            >
              <img
                className='nav-button nav-settings-icon'
                src={'/images/icons/add.svg'}
                alt='icon'
              />
            </Dropdown>}
          </div>}
      </div>}
      <Layout>
        <Sider collapsed={collapsed} onCollapse={(value) => setCollapsed(value)} width={'16rem'} collapsedWidth={56} style={{ background: '#1c1d21' }}>
          {!collapsed ? <div className={'sideNavBar h-full'}>
            <div style={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <div className={`nav-section justify-between pt-5 ${selectedLeftNav !== 'Home' && 'border-none'}`} style={{ flex: '0 0 auto' }} >
                <Popover
                  content={(() => {
                    const palette = getWorkspacePalette(currentWorkspace?.title || '');
                    const initials = (currentWorkspace?.title || 'W').slice(0, 2).toUpperCase();
                    const items = [
                      { key: 'members', label: 'Workspace Members', onClick: () => dispatch(toggleInviteToWorkspaceModal(true)) },
                      { key: 'find', label: 'Find Workspace', onClick: () => navigate('/select-workspace') },
                      { key: 'settings', label: 'Settings', onClick: () => dispatch(isUserDrawerOpen(true)) },
                    ];
                    const btnStyle = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' };
                    return (
                      <div style={{ width: 230, padding: 0 }}>
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 9, background: palette.bg, border: `1px solid ${palette.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 800, color: palette.text, letterSpacing: '0.04em' }}>{initials}</span>
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(15,15,25,0.9)', lineHeight: 1.3 }}>{currentWorkspace?.title}</div>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(99,102,241,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Workspace</div>
                          </div>
                        </div>
                        <div style={{ padding: '6px 8px' }}>
                          {items.map((item) => (
                            <button key={item.key} onClick={item.onClick} style={btnStyle}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.07)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(15,15,25,0.75)' }}>{item.label}</span>
                            </button>
                          ))}
                        </div>
                        <div style={{ padding: '4px 8px 8px', borderTop: '1px solid rgba(99,102,241,0.08)' }}>
                          <button onClick={() => navigate('/create-workspace')} style={btnStyle}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.07)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(99,102,241,0.8)' }}>+ Create Workspace</span>
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  trigger="click"
                  arrow={false}
                  placement='bottomRight'
                >
                  <div className='flex align-center cursor-pointer w-full truncate'>
                    <div className='workspace-header'>{workspaceName}</div>
                    <img
                      className='h-3 w-3'
                      src={'/images/icons/downArrowWhite.svg'}
                      alt='icon'
                    />
                  </div>
                </Popover>
                {toggle && !isMobile && <Tooltip title={intl.formatMessage({ id: 'HIDE_MENU', defaultMessage: 'Hide Menu' })}>
                  <div className='hover:opacity-60'>
                    <Icon name='minimize.svg' onClick={() => {
                      setCollapsed(!collapsed)
                    }} />
                  </div>
                </Tooltip>}
              </div>
              <div className='overflow-y-auto scrollbar-hide flex-1 min-h-0'>
                {(selectedLeftNav === "Home") && homeLinks()}
                <NavBarContainer
                  chats={localChats}
                  teams={localTeams}
                  myProjects={localProjects}
                  workspaces={workspaces}
                  currentWorkspace={currentWorkspace}
                  selectedLeftNav={selectedLeftNav}
                  isGuest={isGuest}
                  setSidebarOpen={setSidebarOpen} />
              </div>
            </div>
            {!isGuest && <div className='d-flex align-center justify-center mb-10' style={{ width: '98%', flex: '0 0' }}>
              <div className='nav-button new-task-nav-button h-8 font-16' onClick={() => setShowTaskModal(true)}>
                <FormattedMessage id="ADD_TASK" defaultMessage="Add task" />
              </div>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'projects-modal',
                      label: <div className='font-16 p-2'><FormattedMessage id="NEW_PROJECT" defaultMessage="New Project" /></div>,
                      onClick: () => setShowProjectModal(true)
                    },
                    {
                      key: 'chats-modal',
                      label: <div className='font-16 p-2'><FormattedMessage id="NEW_CHAT" defaultMessage="New Chat" /></div>,
                      onClick: () => setIsTeamsChatModalOpen(true, false)
                    },
                    {
                      key: 'workspace-modal',
                      label: <div className='font-16 p-2'><FormattedMessage id="NEW_WORKSPACE" defaultMessage="New Workspace" /></div>,
                      onClick: () => navigate('/create-workspace')
                    },
                    // {
                    //   key: 'teams-modal',
                    //   label: <div className='font-16 p-2'>New Team</div>,
                    //   onClick: () => setIsTeamsChatModalOpen(true, true)
                    // }
                  ],
                }}
                placement='top'
                arrow
              >
                <div className='nav-button new-task-nav-button w-8 h-8'>
                  <img
                    className='h-3 w-3'
                    src={'/images/icons/add.svg'}
                    alt='icon'
                  />
                </div>
              </Dropdown>
            </div>}
          </div> :

            <div className='quick-side-menu'>
              {collapsed && !isMobile &&
                <Tooltip title={intl.formatMessage({ id: 'SHOW_MENU', defaultMessage: 'Show Menu' })}>
                  <div className='hover:opacity-60 mb-4 mt-2'>
                    <Icon name='minimize.svg' onClick={() => setCollapsed(false)} />
                  </div>
                </Tooltip>}
              <div className='square-letter'
                onClick={toggleWorkspacePanel}
                style={{
                  color: 'white',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  border: 'none',
                  height: 34,
                  width: 34,
                  fontSize: '12px',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 12px rgba(99,102,241,0.5)',
                  marginTop: toggle ? '0' : '1.5rem',
                  borderRadius: 8,
                }}>
                {iconLetters}
              </div>
              {nav ?
                <SwitchWorkspacePanel toggleWorkspacePanel={toggleWorkspacePanel} workspaces={workspaces} setIsLoading={setIsLoading} />
                :
                <div className='flex flex-col h-full justify-between align-center w-full'>
                  <div>
                    <NavTooltip
                      name={intl.formatMessage({ id: 'HOME', defaultMessage: 'Home' })}
                      src={'home'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Home') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate(`/${currentWorkspace?._id}/`); }} />
                    <div className='h-px bg-white/10 mx-2 my-1' />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'ACTIVITY', defaultMessage: 'Activity' })}
                      src={'mail'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Activity') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/activity'); }}
                      notificationCount={unredNotifications}
                      tooltipComponent={<NotificationTooltip notifications={null} />} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'MY_REMINDERS', defaultMessage: 'Reminders' })}
                      src={'alarm'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Reminders') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/reminders'); }} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'PROJECTS', defaultMessage: 'Projects' })}
                      src={'projects'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Projects') ? 'selected' : ''}`}
                      onClick={() => navTooltipOnClick('Projects')}
                      tooltipComponent={<ProjectsTooltip projects={myProjects} />} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'CHATS', defaultMessage: 'Chats' })}
                      src={'chat-tooltip'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Chats') ? 'selected' : ''}`}
                      onClick={() => navTooltipOnClick('Chats')}
                      notificationCount={unredChatNotifications || 0}
                      tooltipComponent={<ChatsTooltip chats={chats} />} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'TASKS', defaultMessage: 'Tasks' })}
                      src={'tasks-tooltip'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Tasks') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/all-tasks'); }} />
                    <div className='h-px bg-white/10 mx-2 my-1' />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'DASHBOARD', defaultMessage: 'Dashboard' })}
                      src={'dashboardWhite'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Dashboard') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/dashboard'); }} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'PORTFOLIO', defaultMessage: 'Portfolio' })}
                      src={'box'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Portfolio') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/portfolio'); }} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'FORMS', defaultMessage: 'Forms' })}
                      src={'formNavWhite'}
                      classesForParentDiv={`side-nav-item ${isNavActive('Forms') ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); navigate('/forms'); }} />
                    <NavTooltip
                      name={intl.formatMessage({ id: 'SETTINGS', defaultMessage: 'Settings' })}
                      src={'settingsWhite'}
                      classesForParentDiv={`side-nav-item ${selectedLeftNav === 'Settings' ? 'selected' : ''}`}
                      onClick={() => { navTooltipOnClick('Home'); dispatch(isUserDrawerOpen(true)); setSidebarOpen?.(false); }} />
                  </div>
                  {!toggle && <Dropdown
                    trigger={['click']}
                    className='mb-6 h-7'
                    placement='topRight'
                    menu={{
                      items: [
                        {
                          key: 'projects-modal',
                          label: <div className='font-16 p-2'><FormattedMessage id="NEW_PROJECT" defaultMessage="New Project" /></div>,
                          onClick: () => setShowProjectModal(true)
                        },
                        {
                          key: 'chats-modal',
                          label: <div className='font-16 p-2'><FormattedMessage id="NEW_CHAT" defaultMessage="New Chat" /></div>,
                          onClick: () => setIsTeamsChatModalOpen(true, false)
                        },
                        // {
                        //   key: 'teams-modal',
                        //   label: <div className='font-16 p-2'>New Team</div>,
                        //   onClick: () => setIsTeamsChatModalOpen(true, true)
                        // },
                        {
                          key: 'task-modal',
                          label: <div className='font-16 p-2'><FormattedMessage id="NEW_TASK" defaultMessage="New Task" /></div>,
                          onClick: () => setShowTaskModal(true)
                        }
                      ],
                    }}
                  >
                    <img
                      className='nav-button nav-settings-icon'
                      src={'/images/icons/add.svg'}
                      alt='icon'
                    />
                  </Dropdown>}
                </div>}
            </div>
          }

        </Sider>
      </Layout>


    </div>
  );
};

function mapStateToProps(state) {
  return {
    currentWorkspace: state.workspaceReducer.currentWorkspace,
    myProjects: state.projectReducer.myProjects,
    workspaces: state.workspaceReducer.getWorkspaces,
    projectCustomFields: state.projectReducer.projectCustomFields,
    chats: state.chatReducer.chats,
    teams: state.teamReducer.getTeams,
    unreadNotificationCount: state.notificationReducer.unreadTaskCount ?? 0,
    pendingInvites: state.workspaceReducer.pendingWorkspaceInvites,
    workspaceRole: state.userReducer.role
  };
}

export default connect(mapStateToProps)(LeftNavBar);
